/**
 * Second step of sign-in: the one-time code.
 *
 * Kept in its own component so LoginPage stays a form. Two things here are
 * deliberate rather than decorative:
 *   - a delivery failure is shown as a message with a way forward (a backup
 *     code), never as a dead end;
 *   - "remember this browser" is opt-in and dated, so nobody has to guess how
 *     long they just trusted a machine for.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck, KeyRound, ArrowLeft, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export const OtpChallengeForm = ({
  challenge,
  isRTL,
  onVerified,
  onCancel,
  verifyOtp,
  resendOtp,
}) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(challenge.deliveryError || '');
  const [cooldown, setCooldown] = useState(challenge.resendAfter || 60);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [useBackupCode]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await verifyOtp(challenge.challengeId, code, rememberDevice);
      if (result.success) {
        onVerified(result);
      } else {
        setError(result.error);
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    setResending(true);
    try {
      const result = await resendOtp(challenge.challengeId);
      if (result.success) {
        setCooldown(result.resendAfter || 60);
        setNotice(
          result.deliveryError ||
            (challenge.channel === 'email'
              ? isRTL ? 'تم إرسال رمز جديد إلى بريدك الإلكتروني.' : 'A new code has been sent to your email.'
              : isRTL ? 'تم إرسال رمز جديد إلى هاتفك.' : 'A new code has been sent to your phone.')
        );
      } else {
        setError(result.error);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
      data-testid="otp-challenge-form"
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-full bg-brand-orange/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-brand-orange" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-brand-black tracking-tight">
            {isRTL ? 'خطوة تحقق إضافية' : 'One more step'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1" data-testid="otp-target-hint">
            {useBackupCode
              ? isRTL
                ? 'أدخل أحد رموز الاحتياط التي حصلت عليها عند التسجيل.'
                : 'Enter one of the backup codes you were given at enrolment.'
              : isRTL
              ? challenge.channel === 'email'
                ? `أرسلنا رمزاً من ٦ أرقام إلى ${challenge.maskedTarget || 'بريدك الإلكتروني'}`
                : `أرسلنا رمزاً من ٦ أرقام إلى الجوال المنتهي بـ ${challenge.maskedTarget || ''}`
              : challenge.channel === 'email'
              ? `We sent a 6-digit code to ${challenge.maskedTarget || 'your email'}`
              : `We sent a 6-digit code to the phone ending ${challenge.maskedTarget || ''}`}
          </p>
        </div>
      </div>

      {notice && !error && (
        <div
          className="p-3 rounded-lg text-sm font-medium border flex items-start gap-2"
          style={{
            background: 'hsl(38 92% 40% / 0.10)',
            borderColor: 'hsl(38 92% 40% / 0.32)',
            color: 'hsl(30 70% 24%)',
          }}
          data-testid="otp-notice"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {error && (
        <div
          className="p-3 rounded-lg text-sm font-medium border"
          style={{
            background: 'hsl(18 84% 30% / 0.08)',
            borderColor: 'hsl(18 84% 30% / 0.30)',
            color: 'hsl(18 84% 24%)',
          }}
          data-testid="otp-error"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="otp-code" className="text-brand-black font-medium">
            {useBackupCode
              ? isRTL ? 'رمز الاحتياط' : 'Backup code'
              : isRTL ? 'رمز التحقق' : 'Verification code'}
          </Label>
          <Input
            id="otp-code"
            ref={inputRef}
            type="text"
            inputMode={useBackupCode ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => {
              const next = event.target.value;
              // A backup code pasted into the 6-digit field was being silently
              // truncated to "M9CM-7" and then rejected, on the one screen a
              // person reaches when they cannot get in. Anything that is not
              // plain digits switches the field over instead. maxLength stays
              // wide for the same reason: the browser trims a paste before this
              // ever runs, so the six-digit cap is applied here.
              if (!useBackupCode && /[^0-9]/.test(next)) {
                setUseBackupCode(true);
                setCode(next);
                return;
              }
              setCode(useBackupCode ? next : next.slice(0, 6));
            }}
            placeholder={useBackupCode ? 'XXXX-XXXX-XXXX-XXXX' : '000000'}
            maxLength={24}
            required
            disabled={loading}
            className={cn(
              'h-14 bg-card border-border focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-all',
              useBackupCode ? 'text-lg tracking-widest' : 'text-2xl tracking-[0.5em] text-center font-semibold'
            )}
            data-testid="otp-code-input"
            dir="ltr"
          />
        </div>

        <label
          className="flex items-center gap-3 text-sm text-muted-foreground cursor-pointer select-none"
          data-testid="otp-remember-device-label"
        >
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(event) => setRememberDevice(event.target.checked)}
            className="h-4 w-4 rounded border-border accent-[hsl(28_65%_47%)]"
            data-testid="otp-remember-device"
          />
          {isRTL
            ? `لا تسألني على هذا المتصفح لمدة ${challenge.trustedDeviceDays || 7} أيام`
            : `Don't ask on this browser for ${challenge.trustedDeviceDays || 7} days`}
        </label>

        <motion.button
          type="submit"
          disabled={loading || !code}
          whileHover={!loading ? { y: -1 } : {}}
          whileTap={!loading ? { scale: 0.98 } : {}}
          className={cn(
            'w-full h-12 rounded-lg font-semibold text-brand-beige text-base inline-flex items-center justify-center gap-2',
            'bg-brand-black hover:bg-[#2A2624] shadow-brand-md transition-colors',
            'focus:outline-none focus:ring-4 focus:ring-brand-orange/30',
            'disabled:opacity-60 disabled:cursor-not-allowed'
          )}
          data-testid="otp-submit"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {isRTL ? 'جارٍ التحقق…' : 'Verifying…'}
            </>
          ) : (
            <>{isRTL ? 'تأكيد الرمز' : 'Verify code'}</>
          )}
        </motion.button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <button
          type="button"
          onClick={() => {
            setUseBackupCode(!useBackupCode);
            setCode('');
            setError('');
          }}
          className="inline-flex items-center gap-1.5 font-medium text-brand-brown hover:text-brand-orange transition-colors"
          data-testid="otp-toggle-backup-code"
        >
          <KeyRound className="h-3.5 w-3.5" />
          {useBackupCode
            ? challenge.channel === 'email'
              ? isRTL ? 'استخدام الرمز المُرسل بالبريد' : 'Use the emailed code instead'
              : isRTL ? 'استخدام رمز الرسالة النصية' : 'Use the SMS code instead'
            : isRTL ? 'استخدام رمز احتياطي' : 'Use a backup code'}
        </button>

        {!useBackupCode && (
          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0 || resending}
            className="font-medium text-brand-brown hover:text-brand-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="otp-resend"
          >
            {resending
              ? isRTL ? 'جارٍ الإرسال…' : 'Sending…'
              : cooldown > 0
              ? isRTL ? `إعادة الإرسال بعد ${cooldown} ثانية` : `Resend in ${cooldown}s`
              : isRTL ? 'إرسال رمز جديد' : 'Send a new code'}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand-black transition-colors"
        data-testid="otp-cancel"
      >
        <BackIcon className="h-3.5 w-3.5" />
        {isRTL ? 'الرجوع لتسجيل الدخول' : 'Back to sign in'}
      </button>
    </motion.div>
  );
};

export default OtpChallengeForm;
